import { useEffect, useRef, useState } from 'react';
import { useTranslations } from '@/i18n/translations';
import { Button, Row, type ButtonSize } from '@leanin/ui';

// Auto-load shows a loading state for this long before firing the fetch, so
// the next page eases in behind a spinner instead of snapping into place.
// A manual click bypasses it (an explicit action shouldn't feel delayed).
const AUTO_LOAD_DELAY_MS = 1000;

export type LoadMoreProps = {
  readonly hasMore: boolean;
  readonly isLoading?: boolean;
  readonly onClick: () => void;
  readonly size?: Extract<ButtonSize, 'sm' | 'md'>;
  readonly label?: string;
  /**
   * Fire `onClick` automatically when the control scrolls into view, turning
   * the button into infinite scroll. Defaults to `false` (explicit click).
   * Opt in with `autoLoad` only on bottom-anchored forward lists (home feed,
   * resources, directory), where the control sits off-screen until the reader
   * scrolls down to it. Leave it off for top-anchored "load earlier" controls
   * (messages, comments) — an on-screen sentinel there pages backward on open
   * and fights "jump to newest" — and for error-retry buttons (notifications),
   * where `hasMore` includes the error state so auto-firing loops on a failed
   * fetch instead of waiting for a click. The button always stays visible as
   * the click / keyboard / no-observer affordance.
   */
  readonly autoLoad?: boolean;
};

export function LoadMore({
  hasMore,
  isLoading = false,
  onClick,
  size = 'md',
  label,
  autoLoad = false,
}: LoadMoreProps) {
  const t = useTranslations('Common');
  const sentinelRef = useRef<HTMLDivElement>(null);
  // Kept in a ref so the delay timer always calls the latest handler.
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;
  // True between the sentinel entering view and the delayed fetch firing —
  // drives the pre-fetch loading state.
  const [autoPending, setAutoPending] = useState(false);

  // Observe only while a load is possible and none is pending or in flight.
  // Re-arming after `isLoading`/`autoPending` clear is what keeps a short list
  // loading: the fresh observer re-reports the sentinel as still intersecting
  // and queues the next page, until the viewport fills or `hasMore` goes false.
  useEffect(() => {
    if (!autoLoad || !hasMore || isLoading || autoPending) return;
    const el = sentinelRef.current;
    if (el === null) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) setAutoPending(true);
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [autoLoad, hasMore, isLoading, autoPending]);

  // Hold the loading state for AUTO_LOAD_DELAY_MS, then fetch the next page.
  useEffect(() => {
    if (!autoPending) return;
    const timer = setTimeout(() => {
      onClickRef.current();
      setAutoPending(false);
    }, AUTO_LOAD_DELAY_MS);
    return () => clearTimeout(timer);
  }, [autoPending]);

  if (!hasMore) return null;

  // A manual click fetches immediately and cancels any pending auto-delay
  // (clearing `autoPending` tears down the timer via the effect cleanup).
  const handleClick = () => {
    setAutoPending(false);
    onClick();
  };

  return (
    <>
      {autoLoad ? (
        <div ref={sentinelRef} aria-hidden style={{ height: 1 }} />
      ) : null}
      <Row justify="center" pt="lg">
        <Button
          variant="secondary"
          size={size}
          onClick={handleClick}
          loading={isLoading || autoPending}
        >
          {label ?? t('widgets.pagination.loadMore')}
        </Button>
      </Row>
    </>
  );
}
